import { Rectangle } from './../framework/geom/Rectangle';
import { ScrollControl } from './../framework/interactive/ScrollControl';
import { AudioManager, audioManager } from './../objects/AudioManager';
import { soundManager } from './SoundManager';
import { WorldInfo } from './../data/WorldInfo';
import { MathUtil } from './../framework/utils/MathUtil';
import { Button } from './../framework/display/GafManager';
import { LevelInfo } from './../data/LevelInfo';
import { LevelRecord, userRecord } from './../data/UserRecord';
import { DisplayObjectUtil } from './../framework/utils/DisplayObjectUtil';
import { FW } from './../framework/Framework';
import { BaseScreen } from './BaseScreen';


export class Popup extends BaseScreen {

    sprite: GAF.GAFMovieClip;

    graphics: PIXI.Graphics;

    page: number = 0;

    buttons: Array<Button> = [];

    scrollControl: ScrollControl;

    autoScrollStartTime: number;

    tvoRequest: boolean = false;

    constructor() {
        super();

        this.graphics = new PIXI.Graphics();
        this.graphics.beginFill(0x333333, 0.5);
        this.graphics.drawRect(0, 0, FW.stageWidth, FW.stageHeight);
        this.graphics.endFill();
        FW.stage.addChild(this.graphics);

        this.sprite = FW.gafManager.createMovieClip('popups', 'lib_popup');
        this.sprite.interactive = true;
        FW.stage.addChild(this.sprite);

        this.sprite.position.set(FW.stageWidth / 2, FW.stageHeight / 2);
    }

    private createScrollControl(): void {
        this.scrollControl = new ScrollControl(
            new Rectangle(-190, -105, 255, 218)
        );
        this.sprite.addChild(this.scrollControl);
        FW.gafManager.removeLocalTransform(this.sprite['scrollButton']);
        this.scrollControl.initScrollButton(this.sprite['scrollButton'], -105, 73);
        this.scrollControl.setScrollRange(-this.sprite['field'].height + 190, 0, 0);
        this.scrollControl.scrollNode.addChild(this.sprite['field']);
        

        if (this.sprite['field'].height > this.scrollControl.hitArea['height']) {
            this.scrollControl.start();
            FW.addUpdateFunction(this, this.autoScrollUpdate);
            this.autoScrollStartTime = FW.time + 5000;

            this.scrollControl.on(ScrollControl.EVENT.SCROLL_START, () => {
                FW.removeUpdateFunction(this, this.autoScrollUpdate);
            });
        } else {
            this.scrollControl.interactive = false;
            this.sprite['scrollButton'].visible = false;
        }
    }

    private autoScrollUpdate(dt: number) {
        if (FW.time < this.autoScrollStartTime) {
            return;
        }
        if (this.scrollControl.currentScroll <= this.scrollControl.scrollRange.min) {
            FW.removeUpdateFunction(this, this.autoScrollUpdate);
        } else {
            this.scrollControl.setCurrentScroll(this.scrollControl.currentScroll - dt * 0.008, true);
        }
    }

    protected setupButton(clip: PIXI.DisplayObject, callback: () => void, audioType: string): Button {
        let button = super.setupButton(clip, callback, audioType);
        if (button) {
            this.buttons.push(button);
        }
        return button;
    }

    public setTheme(worldCode: string): void {
        this.sprite['bg'].gotoAndStop(worldCode);
        this.sprite['decoration'].gotoAndStop(worldCode);
    }

    dispose(): void {
        if (this.scrollControl) {
            this.scrollControl.dispose();
        }
        FW.removeUpdateFunction(this, this.autoScrollUpdate);
        DisplayObjectUtil.removeFromParent(this.sprite);
        DisplayObjectUtil.removeFromParent(this.graphics);
    }

    basicSetup(worldCode: string, level: LevelInfo, frame: number, delay: number, resolve: any, resolveBack: boolean): void {
        this.sprite.gotoAndStop(frame);
        this.setTheme(worldCode);
        this.sprite['decoration'].visible = frame != 4;
        this.graphics.interactive = true;
        if (level) {
            this.sprite['level'].gotoAndStop(level.isChallenge ? 11 : level.index);
        } else {
            FW.gafManager.removeLocalTransform(this.sprite['decoration']);
            this.sprite['decoration'].y -= 24;
            this.sprite['level'].visible = false;
        }
        if (resolveBack) {
            this.setupButton(this.sprite['mButtonBack'], () => {
                this.close(() => {
                    resolve('back');
                });
            }, 'Back');
        } else {
            this.graphics.on('pointerdown', _event => {
                this.close(() => {
                    resolve();
                }, 250);
            });
            this.setupButton(this.sprite['mButtonBack'], () => {
                this.close(() => {
                    resolve();
                }, 250);
            }, 'Back');
        }

        this.sprite.scale.set(0.1, 0.1);

        let tween: TWEEN.Tween = new TWEEN.Tween(this.sprite.scale);
        tween.to({ "x": 1, "y": 1 }, 1000).easing(TWEEN.Easing.Elastic.Out);

        if (delay) {
            this.graphics.alpha = 0;
            this.sprite.visible = false;
            FW.addDelayFunction(null, () => {
                this.graphics.alpha = 1;
                this.sprite.visible = true;
                tween.start();
            }, null, delay);
        } else {
            tween.start();
        }
        FW.addDelayFunction(null, () => {
            this.createScrollControl();
        }, null, 10);
    }

    private setText(text: string): void {
        let field: GAF.GAFTextField = this.sprite['field'];
        field['textField'].style.lineHeight = 20;
        this.sprite['field'].set_text(' ' + text);
    }

    forWorldSelect(world: WorldInfo): Promise<string> {
        return new Promise<string>((resolve) => {
            this.basicSetup(world.code, null, 1, 0, resolve, false);

            this.sprite['buttonPlay'].visible = false;
            let button = this.setupButton(this.sprite['buttonContinue'], () => {
                this.close(() => {
                    resolve(world.code);
                });
            }, 'Play');

            let audio = audioManager.getWorldAudioUrl(world.index, 1);
            audioManager.stopOthersAndDelayPlayAudio(audio, 1200);

            if (!userRecord.isAudioPlayed(audio)) {
                this.setButtonEnabled(button, false);
                audioManager.on(AudioManager.EVENT.AUDIO_COMPELTE, () => {
                    userRecord.setAudioPlayed(audio);
                    this.setButtonEnabled(button, true);
                });
            }

            this.setupTextsPages(world.descriptions);
        });
    }

    forLevelSelect(level: string): Promise<string> {
        return new Promise<string>((resolve) => {
            let info: LevelInfo = LevelInfo.fromString(level);
            this.basicSetup(info.worldCode, info, 1, 0, resolve, false);
            this.sprite.pivot.set(this.sprite.width / 2, this.sprite.height / 2);

            this.sprite['buttonPlay'].visible = false;
            let button = this.setupButton(this.sprite['buttonContinue'], () => {
                this.close(() => {
                    resolve(level);
                });
            }, 'Play');

            let audio = audioManager.getWorldAudioUrl(info.worldIndex, info.isChallenge ? 33 : info.index * 3 - 1);
            audioManager.stopOthersAndDelayPlayAudio(audio, 1200);

            if (this.tvoRequest && !userRecord.isAudioPlayed(audio)) {
                this.setButtonEnabled(button, false);
                audioManager.on(AudioManager.EVENT.AUDIO_COMPELTE, () => {
                    userRecord.setAudioPlayed(audio);
                    this.setButtonEnabled(button, true);
                });
            }

            this.setupTextsPages(info.calls);
        });
    }

    forLevelInstructions(level: string): Promise<string> {
        return new Promise<string>((resolve) => {
            console.log('forLevelInstructions: ' + level);
            let info: LevelInfo = LevelInfo.fromString(level);
            this.basicSetup(info.worldCode, info, 1, 0, resolve, true);
            this.sprite.pivot.set(this.sprite.width / 2, this.sprite.height / 2);

            this.sprite['buttonContinue'].visible = false;
            let button = this.setupButton(this.sprite['buttonPlay'], () => {
                this.close(() => {
                    resolve(level);
                });
            }, 'Play');

            let audio = audioManager.getWorldAudioUrl(info.worldIndex, info.isChallenge ? 34 : info.index * 3);
            audioManager.stopOthersAndDelayPlayAudio(audio, 1200);
            if (this.tvoRequest && !userRecord.isAudioPlayed(audio)) {
                this.setButtonEnabled(button, false);
                audioManager.on(AudioManager.EVENT.AUDIO_COMPELTE, () => {
                    userRecord.setAudioPlayed(audio);
                    this.setButtonEnabled(button, true);
                });
            }

            this.setupTextsPages(info.instructions);
        });
    }

    private setupTexts(texts: Array<string>, page: number, buttonPrev: Button, buttonNext: Button): void {
        let totalPages: number = texts.length;
        this.page = Math.max(0, Math.min(totalPages - 1, page));

        //texts = texts.concat(texts).concat(texts);

        //this.setText(texts[this.page].replace(/\\n/g, '\n'));
        //buttonPrev.displayObject.visible = this.page > 0;
        //buttonNext.displayObject.visible = totalPages && this.page < totalPages - 1;

        let txt = texts.join('\n\n').replace(/\\n/g, '\n');
        this.setText(txt);
        buttonPrev.displayObject.visible = false;
        buttonNext.displayObject.visible = false;
    }

    private setupTextsPages(texts: Array<string>): void {
        let buttonPrev: Button = this.setupButton(this.sprite['buttonPrev'], () => {
            this.setupTexts(texts, this.page - 1, buttonPrev, buttonNext);
        }, 'Previous');
        let buttonNext: Button = this.setupButton(this.sprite['buttonNext'], () => {
            this.setupTexts(texts, this.page + 1, buttonPrev, buttonNext);
        }, 'Next');

        this.setupTexts(texts, 0, buttonPrev, buttonNext);
    }

    private close(cb: () => void, duration: number = 500): void {
        for (let button of this.buttons) {
            this.setButtonEnabled(button, false, false);
        }

        this.sprite.interactive = false;
        this.sprite.interactiveChildren = false;
        audioManager.stopAll();

        let tween: TWEEN.Tween = new TWEEN.Tween(this.sprite.scale);
        tween.to({ "x": 0.2, "y": 0.2 }, duration).easing(TWEEN.Easing.Back.In);
        tween.onComplete(() => {
            this.dispose();
            cb();
        });
        tween.start();
    }


    forLevelFailed(level: string): Promise<string> {
        return new Promise<string>((resolve) => {
            let info: LevelInfo = LevelInfo.fromString(level);
            this.basicSetup(info.worldCode, info, 2, 1000, resolve, true);
            FW.addDelayFunction(null, () => soundManager.play('level.fail'), null, 1000);
            this.sprite.pivot.set(this.sprite.width / 2, this.sprite.height / 2);

            this.setupButton(this.sprite['buttonAgain'], () => {
                this.close(() => {
                    resolve(level);
                });
            }, 'Retry');

            let message = info.listEndMessages('fail');
            this.setupTextsPages(message.list);
            audioManager.stopOthersAndDelayPlayAudio(message.audio, 2000);
        });
    }

    forLevelComplete(record: LevelRecord): Promise<string> {
        return new Promise<string>((resolve) => {
            let level: LevelInfo = LevelInfo.fromString(record.level);
            this.basicSetup(level.worldCode, level, 4, 0, resolve, true);
            soundManager.play('level.complete');

            this.sprite.pivot.set(this.sprite.width / 2, this.sprite.height / 2);
            if (record.stars) {
                this.sprite['stars'].gotoAndStop(record.stars + 1);
            } else {
                this.sprite['stars'].gotoAndStop(1);
            }

            this.sprite['time'].set_text(MathUtil.formatTimer(record.time, false));
            FW.gafManager.removeLocalTransform(this.sprite['time']);
            this.sprite['time'].y += 12;

            let buttonContinue = this.setupButton(this.sprite['buttonNextLevel'], () => {
                this.close(() => {
                    if (level.isChallenge) {
                        resolve('worlds');
                    } else {
                        resolve(level.getNeighbor(1).level);
                    }
                });
            }, 'Continue');

            if (record.stars < 3) {
                this.setupButton(this.sprite['buttonReplay'], () => {
                    this.close(() => {
                        resolve(record.level);
                    });
                }, 'PlayAgain');
            } else {
                this.sprite['buttonReplay'].visible = false;
            }

            let message = level.listEndMessages(record.stars < 3 ? 'pass' : 'best');
            this.setupTextsPages(message.list);
            audioManager.stopOthersAndDelayPlayAudio(message.audio, 2000);

            if (this.tvoRequest && level.isChallenge && !userRecord.isAudioPlayed(message.audio)) {
                this.setButtonEnabled(buttonContinue, false);
                audioManager.on(AudioManager.EVENT.AUDIO_COMPELTE, () => {
                    userRecord.setAudioPlayed(message.audio);
                    this.setButtonEnabled(buttonContinue, true);
                });
            }
        });
    }
}


// WEBPACK FOOTER //
// ./src/worlds/Popup.ts